import { clock } from './clock';

/**
 * Что не так с готовой записью — до отправки (решение владельца 12.09.2026, §5.1
 * сверки `CHECK-2026-09-11-AUDIO-VOCAB.md`).
 *
 * 🔴 ЗАЧЕМ. Ученица наговорила 2,5 минуты, в файл попало 20 секунд, и отправить
 * его ей ничто не помешало: под записью стояли ЧАСЫ СТРАНИЦЫ, а не длина файла.
 * Теперь файл меряется сам, и заметно короткий отправить нельзя.
 *
 * ⚠️ ДОПУСК 2 СЕКУНДЫ, У СЕРВЕРА 3 (договор `record-integrity.md` §2.3 п. 4).
 * Клиент строже намеренно: сервер не должен отвергать то, что фронт уже пустил.
 *
 * ⚠️ ФАЙЛ ДЛИННЕЕ ЧАСОВ — НЕ БЕДА. Замерено 12.09.2026: Chrome отдаёт запись на
 * секунду-полторы длиннее, чем шли часы. Судить по модулю разницы значило бы
 * закрыть отправку каждому второму ученику на ровном месте.
 */

/** Насколько файл может быть короче часов, чтобы это ещё не считалось обрывом. */
export const CLIENT_TOLERANCE_SECONDS = 2;

export type RecordingVerdictKind =
  | 'ok'
  | 'empty'
  | 'truncated'
  /** Мерили и не смогли, но файл играет — предупреждаем, но отправить даём. */
  | 'unmeasured'
  /** Мерить было нечем (нет WebAudio), файл играет — молчим. */
  | 'unsupported'
  /** Длина не прочлась, и ещё проверяем, играет ли файл, — отправки пока нет. */
  | 'checking'
  /** Файл не получился: начало не опознано или он не играет. Настоять нельзя. */
  | 'broken';

export type RecordingVerdict = {
  kind: RecordingVerdictKind;
  canSave: boolean;
  messageKey?: string;
  params?: Record<string, string>;
};

const BROKEN: RecordingVerdict = { kind: 'broken', canSave: false, messageKey: 'blockRenderers.recordEmptyBroken' };

export function recordingVerdict(input: {
  /** Часы страницы без пауз; `null` — запись не из этой сессии. */
  clientSeconds: number | null;
  /** Длина по звуку; `null` — замер не дал числа. */
  fileSeconds: number | null;
  bytes: number;
  /** Пытались ли мерить вообще. */
  measured: boolean;
  /**
   * Играет ли файл (`lib/audioPlayable.ts`): `null` — ещё проверяем; не передано —
   * не проверяли вовсе (мгновенный вердикт сразу после «Стоп», до замера).
   */
  playable?: boolean | null;
  /** Опознано ли начало файла (`lib/audioSniff.ts`); не передано — не смотрели. */
  recognized?: boolean;
}): RecordingVerdict {
  const { clientSeconds, fileSeconds, bytes, measured, playable, recognized } = input;

  // Пустой файл важнее всего остального: говорить надо про ноль байт, а не про
  // расхождение с часами.
  if (!bytes) {
    return { kind: 'empty', canSave: false, messageKey: 'blockRenderers.recordEmptyLocal' };
  }

  // 🔴 ФАЙЛ БЕЗ НАЧАЛА (13.09.2026). Сервер отвергает его как «неизвестный
  // контейнер» при любой длине — пускать к отправке значит вести в тот же отказ.
  if (recognized === false) return BROKEN;

  if (fileSeconds == null || !Number.isFinite(fileSeconds)) {
    // 🔴 «Сбой замера ответ не отклоняет» — только у файла, который ИГРАЕТ. Вечером
    // 12.09 блок пускал к отправке запись, которую не мог сыграть даже плеер под
    // ней, и ученик ходил по кругу отказов сервера.
    if (playable === false) return BROKEN;
    if (playable === null) return { kind: 'checking', canSave: false };
    // ⚠️ Сбой замера ответ НЕ отклоняет: ученик не должен переписывать работу
    // из-за нашей поломки, сервер померит сам.
    return measured
      ? { kind: 'unmeasured', canSave: true, messageKey: 'blockRenderers.recordUnmeasured' }
      : { kind: 'unsupported', canSave: true };
  }

  if (clientSeconds != null && Number.isFinite(clientSeconds)) {
    const lost = clientSeconds - fileSeconds;
    if (lost > CLIENT_TOLERANCE_SECONDS) {
      return {
        kind: 'truncated',
        canSave: false,
        messageKey: 'blockRenderers.recordTruncatedLocal',
        params: { saved: clock(fileSeconds), total: clock(clientSeconds) },
      };
    }
  }

  return { kind: 'ok', canSave: true };
}
